/**
 * Primitives de mise en page de la vitrine (#808).
 *
 * Rien de plus qu'un gabarit partagé : la largeur de colonne, le rythme vertical
 * et le triptyque « surtitre / titre / chapô » étaient recopiés à l'identique
 * dans chaque section, avec des valeurs qui divergeaient d'une section à l'autre.
 */

import { cn } from '@/shared/utils/cn';

/**
 * Colonne unique de la page — même largeur du bandeau de nav au pied de page.
 *
 * 1280 px et non les 1152 px de l'application : au-delà de 1600 px de viewport,
 * la colonne étroite laissait le hero à moitié vide, avec le visuel mobile perdu
 * dans le dégradé. Un back-office se lit en colonne serrée, une vitrine non.
 */
export const Container = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => <div className={cn('mx-auto w-full max-w-7xl px-5 lg:px-8', className)}>{children}</div>;

/**
 * Bloc de titre d'une section.
 *
 * `tone="dark"` sert les bandeaux sombres : le slate-900 des titres y disparaît,
 * et le teal du surtitre passe sous le seuil de contraste.
 */
export const SectionHeading = ({
  eyebrow,
  title,
  lead,
  tone = 'light',
  align = 'center',
}: {
  eyebrow: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  tone?: 'light' | 'dark';
  align?: 'center' | 'left';
}) => {
  const dark = tone === 'dark';
  return (
    <div
      className={cn(
        'max-w-2xl',
        align === 'center' ? 'mx-auto text-center' : 'text-left',
        align === 'left' && 'max-w-xl',
      )}
    >
      <p
        className={cn(
          'text-[12px] font-bold tracking-widest uppercase',
          dark ? 'text-primary-300' : 'text-primary-600',
        )}
      >
        {eyebrow}
      </p>
      <h2
        className={cn(
          'mt-2.5 text-3xl font-bold tracking-tight sm:text-4xl',
          dark ? 'text-white' : 'text-slate-900',
        )}
      >
        {title}
      </h2>
      {lead && (
        <p
          className={cn(
            'mt-4 text-[17px] leading-relaxed',
            dark ? 'text-white/65' : 'text-slate-600',
          )}
        >
          {lead}
        </p>
      )}
    </div>
  );
};

/** Bouton principal — le seul dégradé plein de la page, réservé à « Demander une démo ». */
export const PrimaryButton = ({
  href,
  className,
  children,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <a
    href={href}
    className={cn(
      'from-primary-400 to-primary-600 shadow-primary-900/30 inline-flex items-center justify-center gap-2',
      'rounded-xl bg-gradient-to-br px-5 py-3 text-[15px] font-semibold text-white shadow-lg',
      'transition-all hover:-translate-y-0.5 hover:shadow-xl',
      className,
    )}
  >
    {children}
  </a>
);
